import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useSendContactEmail } from "@/api/hooks/useInteractions";

interface ComposeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
}

/**
 * Compose + send an outbound email to a contact (Phase 11). Body is a plain
 * textarea (labeled "Message") posted to the API as body_html — the API
 * contract accepts raw text/HTML alike; we don't do any client-side rich
 * text here, matching the "plain textarea is fine" scope note.
 */
export function ComposeEmailDialog({ open, onClose, contactId }: ComposeEmailDialogProps) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const sendEmail = useSendContactEmail();

  useEffect(() => {
    if (open) {
      setSubject("");
      setMessage("");
    }
  }, [open]);

  const canSubmit = !sendEmail.isPending && subject.trim() !== "" && message.trim() !== "";

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await sendEmail.mutateAsync({
        contactId,
        subject: subject.trim(),
        body_html: message.trim(),
      });
      toastSuccess("Email sent");
      onClose();
    } catch (err) {
      toastError("Couldn't send email", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Send email">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Send email</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <FormTextarea
            label="Message"
            required
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={sendEmail.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {sendEmail.isPending ? "Sending..." : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
